import CryptoJS from 'crypto-js'
import qs from 'qs'

// Keeps grid filter values out of the page address. Filter values can name a
// client or a property, and tools that record page addresses (Sentry, browser
// history, server logs, copied links) would then hold them.
//
// Only the keys below stay in the address. They hold no personal data: paging,
// sort, the list a menu link opens (status) and a date range. Their values must
// also have the expected shape. All other values go in the tab's sessionStorage,
// so a refresh and the back button keep them.
//
// A history entry's state holds only a random ID for those values, never the
// values. Code cannot change the state of other history entries, so values there
// would stay after sign-out. clearSavedGridFilters removes every value at once.

export const GRID_ADDRESS_KEYS = Object.freeze(['page', 'perPage', 'sort', 'sortCustom', 'status', 'start_date', 'end_date'])

// history.state key for the ID of the values of one grid page.
export const GRID_STATE_KEY = 'gridFilters'

// history.state key that tells a grid page to restore the values that
// saveGridReturnValues kept (the case page "Go Back" button sets it).
export const GRID_RESTORE_STATE_KEY = 'gridFiltersRestore'

const RETURN_STORAGE_PREFIX = 'grid-filters-return:'
const ENTRY_STORAGE_PREFIX = 'grid-filters-entry:'
// A hash of the login token that the tab's saved values belong to.
const LOGIN_STORAGE_KEY = 'grid-filters-login'

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)

const isEmptyAddressValue = (value) => value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)

const TOKEN = /^[A-Za-z0-9_.-]{1,100}$/
const isToken = (value) => typeof value === 'string' && TOKEN.test(value)
const isWholeNumber = (value) =>
  (typeof value === 'number' && Number.isInteger(value) && value >= 0) || (typeof value === 'string' && /^\d{1,6}$/.test(value))
// ISO dates and times, and DD-MM-YYYY.
const isDate = (value) => typeof value === 'string' && /^\d[\d\-/:.TZ+ ]{0,39}$/.test(value)
const isSortItem = (item) =>
  isPlainObject(item) &&
  Object.keys(item).every((key) => key === 'colId' || key === 'sort') &&
  isToken(item.colId) &&
  ['asc', 'desc'].includes(item.sort)
const isListOf = (check) => (value) => Array.isArray(value) && value.length <= 20 && value.every(check)

const ADDRESS_VALUE_CHECKS = {
  page: isWholeNumber,
  perPage: isWholeNumber,
  sort: isListOf(isSortItem),
  sortCustom: isListOf(isToken),
  status: isToken,
  start_date: isDate,
  end_date: isDate,
}

/** True when `value` may go in the address under `key`. */
export function isGridAddressValue(key, value) {
  return GRID_ADDRESS_KEYS.includes(key) && (isEmptyAddressValue(value) || ADDRESS_VALUE_CHECKS[key](value))
}

/** A plain copy (no Vue proxies) that sessionStorage can hold. */
export function toPlainGridValues(values) {
  return isPlainObject(values) ? JSON.parse(JSON.stringify(values)) : {}
}

/** Splits values into the ones that may go in the address and all others. A value with the wrong shape goes in `other`. */
export function splitGridValues(values) {
  const address = {}
  const other = {}

  for (const [key, value] of Object.entries(isPlainObject(values) ? values : {})) {
    if (isGridAddressValue(key, value)) {
      address[key] = value
    } else {
      other[key] = value
    }
  }

  return { address, other }
}

/** The page address for a grid: the path and the address keys that have a value. */
export function buildGridAddress(path, values) {
  const { address } = splitGridValues(values)
  const kept = Object.fromEntries(Object.entries(address).filter(([, value]) => !isEmptyAddressValue(value)))
  const query = qs.stringify(kept, { encodeValuesOnly: true })

  return query ? `${path}?${query}` : path
}

/**
 * Reads an address that an older version of the app made (with filter values in
 * the query) and returns the clean address and the values it must not show.
 */
export function parseGridAddress(url) {
  const text = typeof url === 'string' ? url.split('#')[0] : ''
  const queryStart = text.indexOf('?')
  const path = queryStart === -1 ? text : text.slice(0, queryStart)
  const values = queryStart === -1 ? {} : qs.parse(text.slice(queryStart + 1))
  const { other } = splitGridValues(values)

  return { path, url: buildGridAddress(path, values), other }
}

/** The clean address for `values` at `path`, and the values that must stay out of it (plain copies). */
export function gridSnapshot(path, values) {
  const plain = toPlainGridValues(values)

  return { url: buildGridAddress(path, plain), other: splitGridValues(plain).other }
}

/**
 * The cases list address in a case page's `currentUrl`, cleaned, or null when it
 * is not a cases list address (so "Go Back" cannot go anywhere else).
 */
export function parseCasesReturnAddress(currentUrl) {
  if (typeof currentUrl !== 'string' || !/^\/cases(?:[/?#]|$)/.test(currentUrl)) return null

  return parseGridAddress(currentUrl)
}

/**
 * The first values of a grid page: the base values, then the page's defaults,
 * then the values saved for this history entry (or, when "Go Back" asks for it,
 * the values kept in sessionStorage), then the address. An address from an older
 * version of the app can still hold filter values; they are read here once.
 */
export function initialGridValues({ defaults, state, path, storage, query } = {}) {
  const saved =
    readGridState(state, path, storage) ?? (shouldRestoreGridReturnValues(state, path) ? readGridReturnValues(storage, path) : null)

  return {
    id: null,
    page: 1,
    perPage: 10,
    filters: {},
    sort: [],
    ...defaults,
    ...saved,
    ...qs.parse(isPlainObject(query) ? query : {}),
  }
}

/** A new random ID for the values of one history entry. It is not a secret. */
export function newGridEntryId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

/** The ID of the values this history entry saved for `path`, or null. */
export function gridEntryId(state, path) {
  const saved = isPlainObject(state) ? state[GRID_STATE_KEY] : null

  return isPlainObject(saved) && saved.path === path && typeof saved.entry === 'string' ? saved.entry : null
}

/** The values a grid page saved for this history entry, or null. */
export function readGridState(state, path, storage) {
  const entry = gridEntryId(state, path)

  return entry ? readStoredValues(storage, ENTRY_STORAGE_PREFIX + entry) : null
}

/** Saves a grid page's values for one history entry. Blocked or full storage means a refresh opens the list without filters. */
export function saveGridEntryValues(storage, entry, values) {
  writeStoredValues(storage, ENTRY_STORAGE_PREFIX + entry, values)
}

/** What a grid page adds to its history entry's state: the ID of its values, never the values. */
export function gridEntryState(path, entry) {
  return { [GRID_STATE_KEY]: { path, entry } }
}

/** True when the history entry asks the grid page at `path` to restore its kept values. */
export function shouldRestoreGridReturnValues(state, path) {
  return isPlainObject(state) && state[GRID_RESTORE_STATE_KEY] === path
}

/** The tab's sessionStorage, or null when the browser blocks it. */
export function gridSessionStorage() {
  try {
    return globalThis.sessionStorage ?? null
  } catch {
    return null
  }
}

/** Keeps a grid page's values for this tab, for "Go Back" from a detail page. */
export function saveGridReturnValues(storage, path, values) {
  writeStoredValues(storage, RETURN_STORAGE_PREFIX + path, values)
}

/**
 * Returns the values kept for `path`, or null. They stay in storage: "Go Back"
 * can be used again from the same case page, and the next save replaces them.
 */
export function readGridReturnValues(storage, path) {
  return readStoredValues(storage, RETURN_STORAGE_PREFIX + path)
}

/** Removes every saved grid filter value of this tab. Call it when the agent's session ends. */
export function clearSavedGridFilters(storage = gridSessionStorage()) {
  try {
    const keys = Array.from({ length: storage?.length ?? 0 }, (_, index) => storage.key(index))

    keys
      .filter((key) => key?.startsWith(RETURN_STORAGE_PREFIX) || key?.startsWith(ENTRY_STORAGE_PREFIX))
      .forEach((key) => storage.removeItem(key))
  } catch {
    // Storage is blocked, so it holds no values.
  }
}

/**
 * Ties the tab's saved values to one login. Values from any other login, or with
 * no login, are removed. This covers a tab that was not running when a session
 * ended, for example one the browser discarded or restored.
 */
export function syncGridFiltersToLogin(token, storage = gridSessionStorage()) {
  const login = token ? CryptoJS.SHA256(token).toString() : null

  try {
    if (login && storage?.getItem(LOGIN_STORAGE_KEY) === login) return

    clearSavedGridFilters(storage)

    if (login) storage?.setItem(LOGIN_STORAGE_KEY, login)
    else storage?.removeItem(LOGIN_STORAGE_KEY)
  } catch {
    // Storage is blocked, so it holds no values.
  }
}

function writeStoredValues(storage, key, values) {
  try {
    storage?.setItem(key, JSON.stringify(toPlainGridValues(values)))
  } catch {
    // Storage is full or blocked. The list then opens without these filters.
  }
}

function readStoredValues(storage, key) {
  try {
    const text = storage?.getItem(key)
    const values = text ? JSON.parse(text) : null

    return isPlainObject(values) ? values : null
  } catch {
    return null
  }
}
